import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { keyBy } from 'lodash-es';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { Button, CustomModal, FilterPickerGroup, MultiSelectFilterPicker, PageEmptyState, PageErrorState, PageHeader, Searchbox, Spinner, TableList, Typography } from 'seagull';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import { ErrorText } from '~/constants';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useLocaleKeys } from '~/i18n';
import { modalSize } from '~/infra/modals';
import { handleRequest } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { useAppConfig, useStore, useStoreActions } from '~/store/store-context';
import NoDataIcon from '../../assets/NoDataIcon';
import { getAccountingDetails } from '../accounting.utils';
import styles from './MapContactsModal.module.scss';
import { ContentState, FooterState, applyFilters, columnDefs, getModalState, getNumMappedContacts, getRelevantSortedContacts, getHeaderItems } from './MapContactsModal.utils';
import MapContactsRow from './MapContactsRow';
// Adapter for react-window
function ContactsRowComponent(props) {
    const { data, index, style } = props;
    const integratedId = data.anchorContacts[index].integratedId({
        accounting: data.accounting
    });
    const integratedAccountingCustomer = integratedId ? data.accountingCustomersDictionary[integratedId] : undefined;
    return /*#__PURE__*/ _jsx("div", {
        style: style,
        children: /*#__PURE__*/ _jsx(MapContactsRow, {
            anchorContact: data.anchorContacts[index],
            accountingCustomers: data.accountingCustomers,
            integratedAccountingCustomer: integratedAccountingCustomer,
            changeContactLink: data.changeContactLink,
            createNewCustomer: data.createNewCustomer,
            testId: 'map-contacts-row',
            onInputFocus: data.onInputFocus,
            onInputBlur: data.onInputBlur,
            accounting: data.accounting
        }, data.anchorContacts[index].id())
    });
}
export function MapContactsModal(props) {
    const { t: texts } = useLocaleKeys('integrations.accounting');
    const { dismissModal, onChange, contactIds, accounting } = props;
    const { contacts, isFetching, error: contactsError, refresh: fetchContacts, dataUpdatedAt: contactsUpdatedAt } = useContacts({
        refreshOnMount: true
    });
    const contactsLoadableState = useMemo(()=>({
            data: contacts,
            error: contactsError,
            loading: isFetching,
            lastSuccessfulFetch: contactsUpdatedAt
        }), [
        contacts,
        contactsError,
        isFetching,
        contactsUpdatedAt
    ]);
    const unsortedAnchorContacts = contacts;
    const accountingCustomersLoadableState = useStore(selectors.integration.accounting(accounting).customers);
    const accountingCustomers = accountingCustomersLoadableState.data;
    const { api, fetchAccountingCustomers, getContactById } = useStoreActions();
    const [anchorContacts, setAnchorContacts] = useState([]);
    const { inputDebounceTime } = useAppConfig();
    const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
    const [search, setSearch] = useState('');
    const [contactMappingStatusFilter, setContactMappingStatusFilter] = useState([]);
    const numOfMappedContacts = useMemo(()=>getNumMappedContacts(anchorContacts, accounting, accountingCustomers), [
        anchorContacts,
        accounting,
        accountingCustomers
    ]);
    const { testIdPrefix } = getAccountingDetails(accounting);
    const modalState = useMemo(()=>{
        return getModalState(contactsLoadableState, accountingCustomersLoadableState);
    }, [
        contactsLoadableState,
        accountingCustomersLoadableState
    ]);
    const accountingCustomersDictionary = useMemo(()=>keyBy(accountingCustomers, (c)=>c.id()), [
        accountingCustomers
    ]);
    const { filteredAnchorContacts, contactMappingStatusFilterOptions } = useMemo(()=>applyFilters(anchorContacts, search, contactMappingStatusFilter, accounting, accountingCustomers || []), [
        anchorContacts,
        search,
        contactMappingStatusFilter,
        accounting,
        accountingCustomers
    ]);
    useEffect(()=>{
        const filteredAndSorted = getRelevantSortedContacts(unsortedAnchorContacts, contactIds || [], accounting);
        setAnchorContacts(filteredAndSorted);
    }, [
        contactIds,
        unsortedAnchorContacts,
        accounting
    ]);
    useEffect(()=>{
        fetchAccountingCustomers(accounting);
    }, []);
    async function refreshData() {
        fetchContacts({
            throwOnError: false
        });
        fetchAccountingCustomers(accounting);
    }
    async function changeContactLink(anchorContact, accountingCustomerId) {
        const link = await handleRequest(api.accounting.linkContact({
            contactId: anchorContact.id(),
            data: {
                integratedId: accountingCustomerId,
                version: anchorContact.version()
            },
            accounting
        }));
        if (!link.success) {
            throw link.error;
        }
        onChange && onChange();
        const updatedContact = await handleRequest(getContactById(anchorContact.id()));
        if (!updatedContact.success) {
            throw updatedContact.error;
        }
        updateContact(updatedContact.data);
    }
    async function createNewCustomer(anchorContact) {
        await api.accounting.createContact({
            contactId: anchorContact.id(),
            accounting
        });
        onChange && onChange();
        const updatedContact = await getContactById(anchorContact.id());
        await fetchAccountingCustomers(accounting);
        updateContact(updatedContact);
    }
    function updateContact(contact) {
        const newAnchorContacts = anchorContacts.map((c)=>c.id() == contact.id() ? contact : c);
        setAnchorContacts(newAnchorContacts);
    }
    function getFooter() {
        switch(modalState.footer){
            case FooterState.Valid:
                return /*#__PURE__*/ _jsxs("div", {
                    className: styles.footer,
                    children: [
                        anchorContacts && /*#__PURE__*/ _jsx("div", {
                            "data-testid": "total-map-status",
                            children: /*#__PURE__*/ _jsx(Typography, {
                                children: "".concat(numOfMappedContacts, " of ").concat(anchorContacts.length, " contacts mapped")
                            })
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            onClick: dismissModal,
                            testId: "".concat(testIdPrefix, "-map-contacts-close"),
                            children: "Close"
                        })
                    ]
                });
            case FooterState.Invalid:
                return null;
        }
    }
    function getContent() {
        switch(modalState.content){
            case ContentState.Valid:
                return /*#__PURE__*/ _jsxs("div", {
                    className: styles.content,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            style: {
                                marginBlockEnd: '24px'
                            },
                            children: /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                                flexWrap: true,
                                testId: "map-contacts-modal-filters",
                                children: [
                                    /*#__PURE__*/ _jsx(Searchbox, {
                                        registerHotkey: registerSearchBoxHotkey,
                                        value: search,
                                        testId: "map-contacts-modal-search",
                                        debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
                                        onChange: (event)=>setSearch(event.target.value),
                                        onClear: ()=>setSearch('')
                                    }),
                                    /*#__PURE__*/ _jsx(MultiSelectFilterPicker, {
                                        testId: "".concat(testIdPrefix, "-mapping-status-filter-picker"),
                                        value: contactMappingStatusFilter,
                                        options: contactMappingStatusFilterOptions,
                                        label: texts.mapContactsIsMapped(),
                                        onChange: setContactMappingStatusFilter
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(TableList.Header, {
                            items: getHeaderItems(accounting, texts)
                        }),
                        filteredAnchorContacts && /*#__PURE__*/ _jsx("div", {
                            className: styles.tableContainer,
                            children: /*#__PURE__*/ _jsx(AutoSizer, {
                                children: (param)=>{
                                    let { height, width } = param;
                                    return /*#__PURE__*/ _jsx(FixedSizeList, {
                                        itemData: {
                                            accounting,
                                            anchorContacts: filteredAnchorContacts,
                                            accountingCustomers,
                                            accountingCustomersDictionary,
                                            createNewCustomer,
                                            changeContactLink,
                                            onInputFocus: ()=>setRegisterSearchBoxHotkey(false),
                                            onInputBlur: ()=>setRegisterSearchBoxHotkey(true)
                                        },
                                        itemCount: filteredAnchorContacts.length,
                                        itemSize: 64,
                                        height: height,
                                        width: width,
                                        overscanCount: 50,
                                        children: ContactsRowComponent
                                    });
                                }
                            })
                        })
                    ]
                });
            case ContentState.Loading:
                return /*#__PURE__*/ _jsx("div", {
                    className: styles.loading,
                    children: /*#__PURE__*/ _jsx(Spinner, {
                        size: 40,
                        testId: "map-contacts-loading"
                    })
                });
            case ContentState.Empty:
                return /*#__PURE__*/ _jsx(PageEmptyState, {
                    testId: "map-contacts-empty-state",
                    illustration: /*#__PURE__*/ _jsx(NoDataIcon, {}),
                    message: texts.mapContactsEmptyMessage({
                        accountingName
                    }),
                    actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                        text: texts.mapContactsCreateAgreement(),
                        hidePrefix: true,
                        testId: "map-contacts-new-btn"
                    })
                });
            case ContentState.Error:
                return /*#__PURE__*/ _jsx(PageErrorState, {
                    testId: "map-contacts-error-state",
                    message: ErrorText.IntegrationDataRetrieval,
                    onRefresh: refreshData,
                    skin: "secondary"
                });
        }
    }
    const accountingName = getAccountingDetails(accounting).fullName;
    return /*#__PURE__*/ _jsx(CustomModal, _object_spread_props(_object_spread({
        testId: "".concat(testIdPrefix, "-map-contacts-modal"),
        footer: getFooter()
    }, modalSize.large), {
        onClose: dismissModal,
        children: /*#__PURE__*/ _jsxs(Typography, {
            className: styles.modalContainer,
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    title: texts.mapContactsToTitle({
                        accountingName
                    }),
                    subheader: texts.mapContactsSubheader({
                        accountingName
                    })
                }),
                /*#__PURE__*/ _jsx(TableList.Provider, {
                    columnDefs: columnDefs,
                    children: getContent()
                })
            ]
        })
    }));
}
